import moment from "moment";
import "moment/locale/da";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Zoom } from "../../App";
import { Button } from "../../Components/Button/Button";
import { CartFailedBanner } from "../../Components/CartFailedBanner/CartFailedBanner";
import { CurrentActiveOrder } from "../../Components/CurrentActiveOrder/CurrentActiveOrder";
import { Icon } from "../../Components/Icon/Icon";
import { OrderNowModal } from "../../Components/OrderNowModal/OrderNowModal";
import { OrdersToday } from "../../Components/OrdersToday/OrdersToday";
import { RecentOrderModal } from "../../Components/RecentOrderModal/RecentOrderModal";
//@ts-ignore
import { da } from "date-fns/locale";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    ActiveOrdersDocument,
    CartsDocument,
    OrdersDocument,
    useAccountSettingsQuery,
    useCartsQuery,
    useOrderCartMutation,
    useOrdersQuery,
    useUpdateCartOrderDateMutation
} from "../../Generated/graphql";
import { skipPollWhenHidden } from "../../Utils/pollWhenVisible";
import "./Orders.css";

registerLocale("da", da);
moment.locale("da");

export const Cart = () => {
  const history = useHistory();
  const [orderNowBox, setOrderNowBox] = useState(false);
  const [windowWidth, setWindowWidth] = useState(768);
  const [scrollValue, setScrollValue] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [recentOrderModal, setRecentOrderModal] = useState<{
    open: boolean;
    orderId: string;
  }>({ open: false, orderId: "" });

  const { data } = useCartsQuery({
    pollInterval: 10000,
    skipPollAttempt: skipPollWhenHidden,
  });
  const { data: orders } = useOrdersQuery();
  const { data: accountSettings } = useAccountSettingsQuery();

  const [sendOrder] = useOrderCartMutation({
    refetchQueries: [{ query: OrdersDocument }, { query: CartsDocument }, { query: ActiveOrdersDocument }],
    awaitRefetchQueries: true,
  });

  const [updateCartDate] = useUpdateCartOrderDateMutation({
    refetchQueries: [{ query: CartsDocument }],
    awaitRefetchQueries: true,
  });

  const [showDatePicker, setShowDatePicker] = useState(false);

  const todaysOrders: number = useMemo(() => {
    if (!orders) {
      return 0;
    }

    const orderedToday = orders.orders.filter(
      (order) => order.orderDate === moment().format("YYYYMMDD")
    );

    return orderedToday.length;
  }, [orders]);

  const updateWindowDimensions = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    updateWindowDimensions();
    window.addEventListener("resize", updateWindowDimensions);

    return () => {
      window.removeEventListener("resize", updateWindowDimensions);
    };
  });

  const currentCart = useMemo(() => {
    if (data && data.carts.length > 0) {
      return data.carts.find((c) => c?.status === "pending") || data.carts[0];
    }
    return null;
  }, [data]);

  const failedCart = useMemo(() => {
    if (data && data.carts.length > 0) {
      return data.carts.find((c) => c?.status === "failed") || null;
    }
    return null;
  }, [data]);

  const addNewItem = () => {
    history.push("/orders/add");
  };

  /**
   * Triggers the ordering of the current cart
   */
  const orderNow = (shouldOrderNowResponse: boolean) => {
    if (!shouldOrderNowResponse) {
      setOrderNowBox(false);
    } else {
      if (!data?.carts[0]) {
        return;
      }

      setOrdering(true);
      sendOrder({ variables: { cartId: data.carts[0].id } })
        .then((response) => {
          setOrderNowBox(false);
          setOrdering(false);

          const result = response.data?.orderCart;
          if (result?.success) {
            if (result.orderNumber) {
              toast(`Ordre ${result.orderNumber} afsendt`, {
                className: "u-toast-success",
                progressClassName: "u-toast-success-bar",
                transition: Zoom,
              });
            } else {
              toast("Ordre afsendt", {
                className: "u-toast-success",
                progressClassName: "u-toast-success-bar",
                transition: Zoom,
              });
            }
          } else {
            toast(result?.error || "Fejl ved afsendelse af ordre", {
              className: "u-toast-error",
              progressClassName: "u-toast-success-bar",
              transition: Zoom,
            });
          }
        })
        .catch((error) => {
          setOrderNowBox(false);
          setOrdering(false);

          if (error.message?.includes("RECENT_ORDER:")) {
            const orderId = error.message.split(":")[1];
            setRecentOrderModal({ open: true, orderId });
          } else if (error.message?.includes("Cart not found")) {
            toast("Kurven er allerede bestilt eller ikke fundet", {
              className: "u-toast-error",
              progressClassName: "u-toast-success-bar",
              transition: Zoom,
            });
          } else {
            toast("Fejl ved afsendelse af ordre", {
              className: "u-toast-error",
              progressClassName: "u-toast-success-bar",
              transition: Zoom,
            });
          }
        });
    }
  };

  const handleForceOrder = () => {
    if (!data?.carts[0]) return;

    setOrdering(true);
    sendOrder({ variables: { cartId: data.carts[0].id, force: true } })
      .then((response) => {
        setRecentOrderModal({ open: false, orderId: "" });
        setOrdering(false);

        const result = response.data?.orderCart;
        if (result?.success) {
          if (result.orderNumber) {
            toast(`Ordre ${result.orderNumber} afsendt`, {
              className: "u-toast-success",
              progressClassName: "u-toast-success-bar",
              transition: Zoom,
            });
          } else {
            toast("Ordre afsendt", {
              className: "u-toast-success",
              progressClassName: "u-toast-success-bar",
              transition: Zoom,
            });
          }
        } else {
          toast(result?.error || "Fejl ved afsendelse af ordre", {
            className: "u-toast-error",
            progressClassName: "u-toast-success-bar",
            transition: Zoom,
          });
        }
      })
      .catch(() => {
        setRecentOrderModal({ open: false, orderId: "" });
        setOrdering(false);
        toast("Fejl ved afsendelse af ordre", {
          className: "u-toast-error",
          progressClassName: "u-toast-success-bar",
          transition: Zoom,
        });
      });
  };

  return (
    <div className="Orders u-no-text-select">
      <RecentOrderModal
        open={recentOrderModal.open}
        orderId={recentOrderModal.orderId}
        onConfirm={handleForceOrder}
        onCancel={() => setRecentOrderModal({ open: false, orderId: "" })}
      />
      <OrderNowModal
        normalSendDate={
          currentCart?.automaticOrderDate
            ? moment(+currentCart.automaticOrderDate).format("LL")
            : ""
        }
        response={orderNow}
        open={orderNowBox}
        ordering={ordering}
      />
      <div className="Orders__header">
        <div className="Orders__title">Dine bestillinger</div>
      </div>
      <div className="Orders__dividerbar" />
      <div
        className="Orders__actions"
        style={{
          boxShadow: scrollValue ? "0 4px 7px 0 rgba(0,0,0,0.15)" : undefined,
          height: scrollValue ? "49px" : "66px",
        }}
      >
        <div className="Orders__actions-backdrop">
          <OrdersToday
            orderError={ordering}
            amountOrderedToday={todaysOrders}
            lastOrderedAt={currentCart?.lastOrderedAt}
          />
          {windowWidth > 1023 && (
            <div className="Orders__next-order-date">
              Næste automatiske bestilling:{" "}
              <b>
                {accountSettings?.accountSettings.hasAutomaticOrdering
                  ? accountSettings?.accountSettings.automaticOrderDays?.length === 0
                    ? "Kun automatisk optælling (manuel bestilling)"
                    : currentCart?.automaticOrderDate &&
                      moment(+currentCart.automaticOrderDate).isValid()
                      ? moment(+currentCart.automaticOrderDate).format(
                          "dddd [d. ] Do MMMM [kl. ] HH"
                        )
                      : "Beregner..."
                  : "Automatisk bestilling deaktiveret"}
              </b>
            </div>
          )}

          <div className="Order__add-items">
            <Button buttonStyle="newItem" width="200px" click={addNewItem}>
              <Icon name="add" width={12} />
              <div>Tilføj varer til bestilling</div>
            </Button>
          </div>
        </div>
      </div>
      <div
        className="Orders__content-wrapper"
        onScroll={(e) => {
          if (!scrollValue && e.currentTarget.scrollTop > 8) {
            setScrollValue(true);
          } else if (scrollValue && e.currentTarget.scrollTop <= 5) {
            setScrollValue(false);
          }
        }}
      >
        {failedCart && <CartFailedBanner cartId={failedCart.id} />}
        {accountSettings?.accountSettings.hasAutomaticOrdering && currentCart?.automaticOrderDate && moment(+currentCart.automaticOrderDate).isValid() && (
          <div className="Orders__order-date">
            Din bestilling til{" "}
            <div className="Order__order-date-next">
              {moment(+currentCart.automaticOrderDate).format(
                "dddd [d. ] Do MMMM"
              )}
            </div>
            <button
              className="Orders__change-date-btn"
              onClick={() => setShowDatePicker(!showDatePicker)}
              title="Ændre bestillingsdato"
            >
              <Icon name="PencilEdit" width={14} height={14} />
            </button>
            {showDatePicker && (
              <div className="Orders__date-picker-container">
                <DatePicker
                  selected={
                    currentCart.automaticOrderDate
                      ? new Date(+currentCart.automaticOrderDate)
                      : new Date()
                  }
                  onChange={(date: Date) => {
                    if (!date || !currentCart) return;
                    const newDateFormatted = moment(date).format("YYYY-MM-DD HH:mm:ssZ");
                    updateCartDate({
                      variables: {
                        cartId: currentCart.id,
                        orderDate: newDateFormatted,
                      },
                    })
                      .then(() => {
                        toast("Bestillingsdato opdateret", {
                          className: "u-toast-success",
                          progressClassName: "u-toast-success-bar",
                          transition: Zoom,
                        });
                        setShowDatePicker(false);
                      })
                      .catch(() => {
                        toast("Fejl ved opdatering af dato", {
                          className: "u-toast-error",
                          progressClassName: "u-toast-success-bar",
                          transition: Zoom,
                        });
                      });
                  }}
                  locale="da"
                  dateFormat="dd/MM/yyyy"
                  minDate={new Date()}
                  inline
                />
              </div>
            )}
            {accountSettings?.accountSettings.autoorderPauseStart && accountSettings?.accountSettings.autoorderPauseEnd && (
              <div className="Orders__pause-notice">
                Automatisk bestilling er sat på pause fra{" "}
                {moment(accountSettings.accountSettings.autoorderPauseStart).format("D. MMM")}{" "}
                til {moment(accountSettings.accountSettings.autoorderPauseEnd).format("D. MMM YYYY")}
              </div>
            )}
          </div>
        )}
        <CurrentActiveOrder
          currentOrder={currentCart}
          windowWidth={windowWidth}
          triggerOrderNow={() => setOrderNowBox(true)}
        />
      </div>
    </div>
  );
};
